import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer'

// Z80: same verified letterhead constants as NotePDF.tsx/TransportNotePDF.tsx -- one real
// address/registration/BIFA text, not re-typed/re-guessed per template. No image logo asset
// exists anywhere in this app (checked) -- every PDF renders "CY UK" as styled text instead.
const CY_ADDRESS_LINES = ['Unit 38 Minworth Industrial Estate', 'Forge Lane', 'Minworth', 'Sutton Coldfield', 'B76 1AH']
const CY_TEL = 'T: +44(0)121 477 4648'
const CY_REG_NO = 'CY UK Limited. Registered in England No. 06056764.'
const BIFA_DISCLAIMER = 'All business is transacted in accordance with our Standard Trading Conditions (copies available on request), incorporating the current British International Freight Association (BIFA) Standard Trading Conditions, which include clauses limiting our liability.'

const S = StyleSheet.create({
  page: { padding: 36, fontFamily: 'Helvetica', fontSize: 9, color: '#222' },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 },
  logoBox: { flex: 1 },
  logoText: { fontSize: 20, fontFamily: 'Helvetica-Bold', color: '#1e3a5f', letterSpacing: 2 },
  addrText: { fontSize: 7.5, color: '#555', marginTop: 2 },
  title: { fontSize: 16, fontFamily: 'Helvetica-Bold', marginBottom: 4, color: '#111' },
  subtitle: { fontSize: 9, color: '#555', marginBottom: 12 },
  metaRow: { flexDirection: 'row', marginBottom: 2 },
  metaLbl: { fontSize: 8, color: '#777', width: 90 },
  metaVal: { fontSize: 8, fontFamily: 'Helvetica-Bold', color: '#222' },
  table: { marginTop: 14, marginBottom: 10 },
  thead: { flexDirection: 'row', backgroundColor: '#1e3a5f', padding: '4 6' },
  trow: { flexDirection: 'row', padding: '4 6', borderBottomWidth: 0.5, borderBottomColor: '#f3f4f6' },
  trowAlt: { flexDirection: 'row', padding: '4 6', borderBottomWidth: 0.5, borderBottomColor: '#f3f4f6', backgroundColor: '#f9fafb' },
  th: { fontSize: 7.5, fontFamily: 'Helvetica-Bold', color: '#fff', textTransform: 'uppercase' },
  td: { fontSize: 8 },
  colCode: { width: '25%' }, colDesc: { width: '35%' }, colUnit: { width: '20%' }, colPrice: { width: '20%', textAlign: 'right' },
  footer: { marginTop: 20, borderTopWidth: 0.5, borderTopColor: '#d1d5db', paddingTop: 8 },
  disclaimer: { fontSize: 6.5, color: '#888', marginTop: 6, lineHeight: 1.3 },
})

export interface RateCardPdfLine {
  chargeCode: string
  description: string
  unit: string
  unitPrice: number
}

export interface RateCardPdfProps {
  rateCardName: string
  customerName: string | null
  modeName: string
  laneName: string | null
  effectiveFrom: string
  effectiveTo: string | null
  currency: string
  lines: RateCardPdfLine[]
}

// Z80 Phase 1: customer-facing export -- public fields only (unit price), no cost column, no
// internal notes. Matches the "Export CSV (给客户)" ask's intent of a customer-safe document,
// just as a PDF rather than CSV since Gary asked for both -- CSV export is a plain data file
// via naFileExporter.ts's download pattern (see pricingExport.ts), this PDF is the
// presentation-quality version with CY UK's letterhead.
export default function RateCardPDF({ rateCardName, customerName, modeName, laneName, effectiveFrom, effectiveTo, currency, lines }: RateCardPdfProps) {
  return (
    <Document>
      <Page size="A4" style={S.page}>
        <View style={S.header}>
          <View style={S.logoBox}>
            <Text style={S.logoText}>CY UK</Text>
          </View>
          <View>
            {CY_ADDRESS_LINES.map((line, i) => <Text key={i} style={S.addrText}>{line}</Text>)}
            <Text style={S.addrText}>{CY_TEL}</Text>
          </View>
        </View>

        <Text style={S.title}>{rateCardName}</Text>
        <Text style={S.subtitle}>Rate Card{customerName ? ` — ${customerName}` : ' — Standard Rate'}</Text>

        <View style={S.metaRow}><Text style={S.metaLbl}>Mode</Text><Text style={S.metaVal}>{modeName}</Text></View>
        <View style={S.metaRow}><Text style={S.metaLbl}>Lane</Text><Text style={S.metaVal}>{laneName || 'All lanes'}</Text></View>
        <View style={S.metaRow}><Text style={S.metaLbl}>Effective From</Text><Text style={S.metaVal}>{effectiveFrom}</Text></View>
        {effectiveTo && <View style={S.metaRow}><Text style={S.metaLbl}>Effective To</Text><Text style={S.metaVal}>{effectiveTo}</Text></View>}
        <View style={S.metaRow}><Text style={S.metaLbl}>Currency</Text><Text style={S.metaVal}>{currency}</Text></View>

        <View style={S.table}>
          <View style={S.thead}>
            <Text style={[S.th, S.colCode]}>Code</Text>
            <Text style={[S.th, S.colDesc]}>Description</Text>
            <Text style={[S.th, S.colUnit]}>Unit</Text>
            <Text style={[S.th, S.colPrice]}>Price ({currency})</Text>
          </View>
          {lines.map((l, i) => (
            <View key={i} style={i % 2 === 0 ? S.trow : S.trowAlt}>
              <Text style={[S.td, S.colCode]}>{l.chargeCode}</Text>
              <Text style={[S.td, S.colDesc]}>{l.description}</Text>
              <Text style={[S.td, S.colUnit]}>{l.unit}</Text>
              <Text style={[S.td, S.colPrice]}>{l.unitPrice.toFixed(2)}</Text>
            </View>
          ))}
        </View>

        <View style={S.footer}>
          <Text style={S.addrText}>{CY_REG_NO}</Text>
          <Text style={S.disclaimer}>{BIFA_DISCLAIMER}</Text>
        </View>
      </Page>
    </Document>
  )
}
